import { useQuery } from "@tanstack/react-query";

import { BestDealOffer } from "@/components/marketplace/best-deal-offer";
import { DealOfferExplorerModal } from "@/components/marketplace/deal-offer-explorer-modal";
import { DealOfferHistoryButton } from "@/components/marketplace/deal-offer-history-button";
import { DealOfferStoreDisclosure } from "@/components/marketplace/deal-offer-panel-footer";
import { getDealOffersQueryOptions } from "@/components/marketplace/deal-offers-query";
import type { DealOffer, DealOffersProps } from "@/components/marketplace/deal-offers-types";
import { ReleaseOfferAlternatives } from "@/components/marketplace/ReleaseOfferAlternatives";
import { selectReleaseOffers } from "@/components/marketplace/selectReleaseOffers";
import { UsedOfferSection } from "@/components/marketplace/UsedOfferSection";
import { useCurrency } from "@/hooks/use-currency";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";

function usedOffersInput(
  { workId, format }: Pick<DealOffersProps, "workId" | "format">,
  releaseId: string,
  currency: string,
) {
  return workId ? { workId, currency, format } : { releaseId, currency };
}

/** The selected edition owns the hero; work offers are secondary and share one explorer. */
export function ReleaseDealOffersContent({
  contextReleaseId,
  releaseId,
  workId,
  format,
  resolvedHeader,
  resolvedErrorState,
  resolvedLoadingState,
}: DealOffersProps) {
  const id = contextReleaseId ?? releaseId!;
  const { currency } = useCurrency();
  const { viewerId } = useViewerQueryState();
  const modal = useHashModal();
  const ownerId = `release:${id}`;
  const queryInput = workId ? { workId, currency } : { releaseId: id, currency };
  const offersQuery = useQuery(getDealOffersQueryOptions(queryInput, { viewerId }));
  const offers = (offersQuery.data?.offers ?? []) as DealOffer[];
  const { releaseOffers, alternatives } = selectReleaseOffers(offers, id);
  const bestOffer = releaseOffers[0];
  const explorerOffer = bestOffer ?? offers[0];
  const showEmptyExplorer = [!bestOffer, offers.length > 1].every(Boolean);
  const openExplorer = () => {
    if (explorerOffer)
      modal.open(
        "offer-explorer",
        {
          offer: explorerOffer,
          format: format === "all" ? undefined : format,
        },
        { ownerId },
      );
  };

  if (offersQuery.isLoading)
    return (
      <>
        {resolvedHeader}
        {resolvedLoadingState}
      </>
    );
  if (offersQuery.isError)
    return (
      <>
        {resolvedHeader}
        {resolvedErrorState}
      </>
    );

  return (
    <>
      {resolvedHeader}
      <div hidden={offers.length === 0}>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {bestOffer && (
            <BestDealOffer
              offer={bestOffer}
              offers={offers}
              contextReleaseId={id}
              currency={currency}
              presentationScope="release"
              onExplore={openExplorer}
              allEditions
              className="rounded-none border-0"
            />
          )}
          <ReleaseOfferAlternatives
            alternatives={alternatives}
            hasOffer={Boolean(bestOffer)}
            workId={workId}
          />
          {showEmptyExplorer && (
            <div className="border-t border-border px-4 py-2 sm:px-6">
              <DealOfferHistoryButton
                variant="link"
                mode="offers"
                count={offers.length}
                onClick={openExplorer}
              />
            </div>
          )}
        </div>
      </div>
      <UsedOfferSection
        input={usedOffersInput({ workId, format }, id, currency)}
        contextReleaseId={id}
        hasNewOffers={Boolean(bestOffer)}
        showStoreDisclosure={offers.length === 0}
      />
      <div hidden={offers.length === 0}>
        <DealOfferStoreDisclosure />
      </div>
      <DealOfferExplorerModal
        offers={offers}
        contextReleaseId={id}
        releaseId={id}
        workId={workId}
        currency={currency}
        ownerId={ownerId}
        queryInput={queryInput}
      />
    </>
  );
}
